import React, { useState } from "react";
import { useParams } from "react-router-dom";
import MainLayout from "../layouts/MainLayout";
import BusinessLayout from "../layouts/BusinessLayout";
import { OrganizationHero } from "../components/organization";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { PhoneInput } from "../components/ui/phone-input";
import { isValidPhoneNumber } from "react-phone-number-input";
import Map from "../components/common/Map";
import placeholder from "../assets/images/placeholder.webp";
import { useI18n } from "../hooks/useI18n";
import { useInstitutionalOrganization } from "../hooks/useInstitutionalOrganization";
import { buildWhatsAppUrl, selectDeterministicItem } from "../domain/shared/commerce.utils";
import type { OrganizationPageData } from "../presentation/organizations/organizationPage.types";
import type { InstitutionalPageData } from "../presentation/organizations/institutionalPage.types";

interface ContactContentProps {
  business?: OrganizationPageData;
  institutional?: InstitutionalPageData;
}

const ContactContent: React.FC<ContactContentProps> = ({ business, institutional }) => {
  const { text, t } = useI18n();

  const contactInfo = business?.contactInfo ?? institutional?.contactInfo ?? {};
  const socialMedia = business?.socialMedia ?? institutional?.socialMedia ?? {};
  const entityName = business?.name ?? institutional?.name ?? "";

  const backgroundImage = business
    ? selectDeterministicItem(business.images, business.id)?.url ?? placeholder
    : placeholder;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState<string>("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const FORM_ENDPOINT = process.env.REACT_APP_CONTACT_FORM_ENDPOINT;

  const validate = () => {
    if (!name.trim()) {
      setErrorMsg(text.contact.errors.name);
      return false;
    }
    if (!email.trim() || !/^\S+@\S+\.\S+$/.test(email)) {
      setErrorMsg(text.contact.errors.email);
      return false;
    }
    if (!phone || !isValidPhoneNumber(phone)) {
      setErrorMsg(text.contact.errors.phone);
      return false;
    }
    if (!message.trim()) {
      setErrorMsg(text.contact.errors.message);
      return false;
    }
    setErrorMsg(null);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      setStatus("error");
      return;
    }

    if (!FORM_ENDPOINT) {
      setStatus("error");
      setErrorMsg(text.contact.errors.endpoint);
      return;
    }

    setSubmitting(true);
    setStatus("idle");

    try {
      const payload = new URLSearchParams();
      payload.append("name", name);
      payload.append("email", email);
      payload.append("phone", phone);
      payload.append("message", message);

      if (business) {
        payload.append("businessId", business.id);
        payload.append("businessName", business.name);
      }

      const res = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: payload.toString(),
      });

      if (!res.ok) {
        const responseText = await res.text();
        throw new Error(responseText || text.contact.errors.submit);
      }

      setStatus("success");
      setName("");
      setEmail("");
      setPhone("");
      setMessage("");
    } catch (err: unknown) {
      setStatus("error");
      setErrorMsg(err instanceof Error ? err.message : text.contact.errors.submit);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <OrganizationHero
        name={t(text.contact.heroTitle, { name: entityName })}
        description={text.contact.heroSubtitle}
        heroImage={backgroundImage}
        height="medium"
      />

      <section className="bg-background">
        <div className="container mx-auto flex min-h-[70vh] max-w-2xl flex-col justify-center py-16">
          <h2 className="mb-8 text-center text-3xl font-semibold">{text.contact.infoTitle}</h2>

          <div className="mb-12 space-y-2 text-center text-muted-foreground">
            {contactInfo.address && <p>
              <strong>{text.contact.address}:</strong> {contactInfo.address}
            </p>}
            {contactInfo.phone && <p>
              <strong>{text.contact.phone}:</strong> {contactInfo.phone}
            </p>}
            {contactInfo.email && <p>
              <strong>{text.contact.email}:</strong> {contactInfo.email}
            </p>}
          </div>

          {business && <div className="mb-8">
              <Map
                markers={[
                  business.location
                    ? { lat: business.location.lat, lng: business.location.lng, title: business.name, description: business.contactInfo.address }
                    : { lat: 9.6333, lng: -83.5167, title: business.name, description: business.contactInfo.address },
                ]}
                single
                height="320px"
              />
          </div>}

          <form className="space-y-6" onSubmit={handleSubmit} noValidate>
            <div>
              <Label htmlFor="name">{text.contact.name}</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder={text.contact.fullName} aria-required />
            </div>

            <div>
              <Label htmlFor="email">{text.contact.email}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={text.contact.emailPlaceholder}
                aria-required
              />
            </div>

            <div>
              <Label htmlFor="phone">{text.contact.phone}</Label>
              <PhoneInput
                id="phone"
                value={phone}
                onChange={(value) => setPhone((value as string) || "")}
                placeholder={text.contact.phonePlaceholder}
                defaultCountry="CR"
                international
                aria-required
              />
              <p className="mt-1 text-xs text-muted-foreground">{text.contact.phoneHint}</p>
            </div>

            <div>
              <Label htmlFor="message">{text.contact.message}</Label>
              <Textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={text.contact.messagePlaceholder}
                rows={5}
                aria-required
              />
            </div>

            <div>
              <Button type="submit" className="w-full text-white" disabled={submitting} aria-disabled={submitting}>
                {submitting ? text.contact.sending : text.contact.send}
              </Button>
            </div>

            {status === "success" && <div role="status" className="mt-4 text-center text-green-600">{text.contact.success}</div>}
            {status === "error" && <div role="alert" className="mt-4 text-center text-red-600">{errorMsg || text.contact.errors.submit}</div>}
          </form>

          {socialMedia.whatsapp && (
            <div className="mt-8 text-center">
              <Button asChild variant="outline">
                <a href={buildWhatsAppUrl(socialMedia.whatsapp)} target="_blank" rel="noopener noreferrer">
                  {text.contact.whatsapp}
                </a>
              </Button>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

const InstitutionalContact: React.FC = () => {
  const institutional = useInstitutionalOrganization();
  return (
    <MainLayout>
      <ContactContent institutional={institutional} />
    </MainLayout>
  );
};

const Contact: React.FC = () => {
  const { businessId } = useParams<{ businessId?: string }>();

  if (!businessId) return <InstitutionalContact />;

  return (
    <BusinessLayout>
      {(business) => <ContactContent business={business} />}
    </BusinessLayout>
  );
};

export default Contact;
